/* eslint-disable max-lines */
import { Route, Routes } from 'react-router-dom';
import Loadable from 'react-loadable';
import ErrorBoundary from '../../components/errors/ErrorBoundary';

import { LoadingIndicator, LoadingIndicatorSkeleton } from 'modules/core/components/loader';
import { withTitle } from 'modules/core/components/title';
import UserHasPermissionV2 from 'modules/permissions/abilities/userHasPermissionV2';
import DashBoard from 'modules/dashboard';
import LoginHomePage from 'modules/auth/LoginHomePage';
import CarmenMakeCompatTable from 'modules/carmen/CarmenMakeCompatTable';
import { ClientSearchPage } from 'modules/clients/ClientSearchPage';
import ClientPage from 'modules/clients/ClientPage';
import CarmenUsers from 'modules/carmen/CarmenUsers';
import VehiclePage from '../../modules/vehicles/components/VehiclePage';
import VehicleSearchByNumber from '../../modules/vehicles/components/VehicleSearchByNumber';
import * as URL from './url';

/**
 * Обёртка над ErrorBoundary для роутов.
 * Позволяет изолировать падение тяжёлых модулей (orders, scheduler)
 * от глобального SPA — пользователь видит fallback вместо белого экрана.
 */
const safe = (element, name) => (
  <ErrorBoundary name={ name }>
    { element }
  </ErrorBoundary>
);

const NoMatch = withTitle({
  title: 'Не найдено',
  component: Loadable({
    loader: () => import('../../components/errors/404'),
    loading: LoadingIndicator,
  }),
});

const ResetPassword = withTitle({
  title: 'Сброс пароля',
  component: Loadable({
    loader: () => import('../../modules/auth/ResetPassword'),
    loading: LoadingIndicator,
  }),
});
const SignUpUser = withTitle({
  title: 'Регистрация',
  component: Loadable({
    loader: () => import('../../modules/auth/SignUp'),
    loading: LoadingIndicator,
  }),
});

const UsersList = withTitle({
  title: 'Пользователи',
  component: Loadable({
    loader: () => import('../../modules/users/Users'),
    loading: LoadingIndicatorSkeleton,
  }),
});
const UserEditOne = Loadable({
  loader: () => import('../../modules/users/UserEdit'),
  loading: LoadingIndicator,
});

const PartnersList = withTitle({
  title: 'Партнёры',
  component: Loadable({
    loader: () => import('../../modules/partners/Partners'),
    loading: LoadingIndicatorSkeleton,
  }),
});
const PartnerDivisionList = withTitle({
  title: 'Подразделения',
  component: Loadable({
    loader: () => import('../../modules/partners/components/PartnerDivisionList'),
    loading: LoadingIndicatorSkeleton,
  }),
});
const PartnerCreateOne = Loadable({
  loader: () => import('../../modules/partners/PartnerCreate'),
  loading: LoadingIndicator,
});
const PartnerEditOne = Loadable({
  loader: () => import('../../modules/partners/PartnerEdit'),
  loading: LoadingIndicator,
});
const InsuranceList = withTitle({
  title: 'Страховые',
  component: Loadable({
    loader: () => import('../../modules/insurances/insuranceList'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const InsuranceChanges = withTitle({
  title: 'Изменения в страховых',
  component: Loadable({
    loader: () => import('../../modules/insurances/insuranceChanges'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const DocTemplatesPage = withTitle({
  title: 'Бланки для автозаполнения',
  component: Loadable({
    loader: () => import('../../modules/partners/DocTemplatesPage'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const VehicleChanges = withTitle({
  title: 'Изменения в автомобилях',
  component: Loadable({
    loader: () => import('../../modules/vehicle-confirmation/vehicleChanges'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const OrdersList = withTitle({
  title: 'Заявки',
  component: Loadable({
    loader: () => import('../../modules/orders/Orders'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const EvacuatorOrders = withTitle({
  title: 'Эвакуация+ТП',
  component: Loadable({
    loader: () => import('../../modules/orders/EvacuatorOrders'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const AvarcomOrders = withTitle({
  title: 'Аваркомы',
  component: Loadable({
    loader: () => import('../../modules/orders/AvarcomOrders'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const TBankAvarcomOrders = withTitle({
  title: 'Т-банк Аваркомы',
  component: Loadable({
    loader: () => import('../../modules/orders/TBankAvarcomOrders'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const TBankEvacuationOrders = withTitle({
  title: 'Т-банк эвакуация',
  component: Loadable({
    loader: () => import('../../modules/orders/TBankEvacuationOrders'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const OrdersByValues = withTitle({
  title: 'Заявки по значениям',
  component: Loadable({
    loader: () => import('../../modules/orders/OrdersByValues'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const UsersOnline = withTitle({
  title: 'Сотрудники online',
  component: Loadable({
    loader: () => import('../../modules/users/UserStateList'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const TireFittingOrders = withTitle({
  title: 'Шинный сервис',
  component: Loadable({
    loader: () => import('../../modules/orders/TireFittingOrders'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const LossAdjustmentOrders = withTitle({
  title: 'Урегулирование убытков',
  component: Loadable({
    loader: () => import('../../modules/orders/LossAdjustmentOrders'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const StatusNoticeList = withTitle({
  title: 'Возвраты совкомбанку',
  component: Loadable({
    loader: () => import('../../modules/scheduler/components/statusNotice/StatusNoticeList'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const OrderCommunicationExpired = withTitle({
  title: 'Контакты',
  component: Loadable({
    loader: () => import('../../modules/orders/components/OrderCommunicationExpired'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const OrderCommunicationExpiredByOffice = withTitle({
  title: 'Контакты по территории',
  component: Loadable({
    loader: () => import('../../modules/orders/components/OrderCommunicationExpiredByOffice'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const OrderCreateOne = Loadable({
  loader: () => import('../../modules/orders/OrderCreate'),
  loading: LoadingIndicatorSkeleton,
});
const OrderEditOne = Loadable({
  loader: () => import('../../modules/orders/OrderEdit'),
  loading: LoadingIndicatorSkeleton,
});

const OrderEditEvacuatorOne = Loadable({
  loader: () => import('../../modules/orders/components/evacuation/OrderEditEvacuation'),
  loading: LoadingIndicatorSkeleton,
});

const OrderEditTechServiceOne = Loadable({
  loader: () => import('../../modules/orders/components/techservice/OrderEditTechService'),
  loading: LoadingIndicatorSkeleton,
});

const OrderEditTireFittingOne = Loadable({
  loader: () => import('../../modules/orders/components/tirefitting/OrderEditTireFitting'),
  loading: LoadingIndicatorSkeleton,
});

const OrderEditTireStorageOne = Loadable({
  loader: () => import('../../modules/orders/components/tireStorage/OrderEditTireStorage'),
  loading: LoadingIndicatorSkeleton,
});

const OrderEditLossAdjustmentOne = Loadable({
  loader: () => import('../../modules/orders/components/loss/OrderEditLossAdjustment'),
  loading: LoadingIndicatorSkeleton,
});

const OrderDocPreviewPage = Loadable({
  loader: () => import('../../modules/orders/DocPreviewPage'),
  loading: LoadingIndicatorSkeleton,
});

const VzrPoliciesList = withTitle({
  title: 'ВЗР Полисы',
  component: Loadable({
    loader: () => import('../../modules/vzr/policies/Policies'),
    loading: LoadingIndicatorSkeleton,
  }),
});
const VzrPolicyCreateOne = Loadable({
  loader: () => import('../../modules/vzr/policies/PolicyCreate'),
  loading: LoadingIndicator,
});
const VzrPoliciesViewOne = Loadable({
  loader: () => import('../../modules/vzr/policies/PolicyEdit'),
  loading: LoadingIndicator,
});
const VzrPaymentsList = withTitle({
  title: 'ВЗР Платежи',
  component: Loadable({
    loader: () => import('../../modules/vzr/payments/Payments'),
    loading: LoadingIndicatorSkeleton,
  }),
});
const VzrPaymentEditOne = Loadable({
  loader: () => import('../../modules/vzr/payments/PaymentEdit'),
  loading: LoadingIndicatorSkeleton,
});
const VzrPromoCodesList = withTitle({
  title: 'ВЗР Промокоды',
  component: Loadable({
    loader: () => import('../../modules/vzr/promocodes/PromoCodes'),
    loading: LoadingIndicatorSkeleton,
  }),
});
const VzrPromoCodeViewOne = Loadable({
  loader: () => import('../../modules/vzr/promocodes/PromoCodeEdit'),
  loading: LoadingIndicator,
});

const CommonConfig = Loadable({
  loader: () => import('../../modules/config/CommonConfig'),
  loading: LoadingIndicator,
});

const WorkspaceConfig = Loadable({
  loader: () => import('../../modules/config/WorkspaceConfig'),
  loading: LoadingIndicator,
});

const CronTaskPage = withTitle({
  title: 'Кронтаски',
  component: Loadable({
    loader: () => import('../../modules/config/CronTaskPage'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const PersonalStatistic = Loadable({
  loader: () => import('../../modules/users/PersonalStatistic'),
  loading: LoadingIndicator,
});

const StreamBalance = Loadable({
  loader: () => import('../../modules/users/StreamBalance'),
  loading: LoadingIndicator,
});

const MaxChannels = Loadable({
  loader: () => import('../../modules/users/MaxChannels'),
  loading: LoadingIndicator,
});

const WebsocketLog = Loadable({
  loader: () => import('../../modules/config/WebSocketForm'),
  loading: LoadingIndicator,
});

const Services = withTitle({
  title: 'Сервисы',
  component: Loadable({
    loader: () => import('../../modules/permissions/Services'),
    loading: LoadingIndicator,
  }),
});
const Positions = withTitle({
  title: 'Позиции номенклатуры',
  component: Loadable({
    loader: () => import('../../modules/prices/PositionList'),
    loading: LoadingIndicator,
  }),
});
const PriceLists = withTitle({
  title: 'Прайс-листы',
  component: Loadable({
    loader: () => import('../../modules/prices/PriceLists'),
    loading: LoadingIndicator,
  }),
});
const PriceListOne = Loadable({
  loader: () => import('../../modules/prices/PriceListEditOne'),
  loading: LoadingIndicatorSkeleton,
});
const ReconciliationEditOne = Loadable({
  loader: () => import('../../modules/reconciliations/ReconciliationEdit'),
  loading: LoadingIndicatorSkeleton,
});
const Roles = withTitle({
  title: 'Роли',
  component: Loadable({
    loader: () => import('../../modules/permissions/Roles'),
    loading: LoadingIndicator,
  }),
});
const RoleEdit = withTitle({
  title: 'Редактирование роли',
  component: Loadable({
    loader: () => import('../../modules/permissions/RoleEdit'),
    loading: LoadingIndicator,
  }),
});
const Operations = withTitle({
  title: 'Операции',
  component: Loadable({
    loader: () => import('../../modules/permissions/Operations'),
    loading: LoadingIndicator,
  }),
});
const Permissions = withTitle({
  title: 'Права по ролям',
  component: Loadable({
    loader: () => import('../../modules/permissions/PermissionsListV2'),
    loading: LoadingIndicator,
  }),
});
const PartnerPermissions = withTitle({
  title: 'Разрешения партнёров',
  component: Loadable({
    loader: () => import('../../modules/permissions/PartnerPermissions'),
    loading: LoadingIndicator,
  }),
});
const Feedback = withTitle({
  title: 'Обратная связь',
  component: Loadable({
    loader: () => import('../../modules/feedback/Feedback'),
    loading: LoadingIndicator,
  }),
});
const MyFeedback = withTitle({
  title: 'Мои обращения',
  component: Loadable({
    loader: () => import('../../modules/feedback/MyFeedback'),
    loading: LoadingIndicator,
  }),
});
const PermissionByOperation = withTitle({
  title: 'Права по операции',
  component: Loadable({
    loader: () => import('../../modules/permissions/PermissionByOperations'),
    loading: LoadingIndicator,
  }),
});
const UniversalReference = withTitle({
  title: 'Универсальный справочник',
  component: Loadable({
    loader: () => import('../../modules/universal-reference/UniversalReference'),
    loading: LoadingIndicator,
  }),
});

const A3AvailabilityList = Loadable({
  loader: () => import('../../modules/reports/components/a3Availability'),
  loading: LoadingIndicator,
});

const ClientRegistrationReport = Loadable({
  loader: () => import('../../modules/reports/components/ClientRegistrationReport'),
  loading: LoadingIndicator,
});

const MobileStatisticsReport = Loadable({
  loader: () => import('../../modules/reports/components/MobileStatisticsReport'),
  loading: LoadingIndicator,
});

const MobileStatisticsReportV2 = Loadable({
  loader: () => import('../../modules/reports/components/MobileStatisticsReportV2'),
  loading: LoadingIndicator,
});

const BonusesReport = Loadable({
  loader: () => import('../../modules/reports/components/BonusesReport'),
  loading: LoadingIndicator,
});

const A3PaidFineList = Loadable({
  loader: () => import('../../modules/reports/components/a3PaidFine'),
  loading: LoadingIndicator,
});

const SettlementWithPartnerReport = Loadable({
  loader: () => import('../../modules/reports/components/settlementWithPartnerReport'),
  loading: LoadingIndicator,
});

const AppointmentJournalUtm = Loadable({
  loader: () => import('../../modules/reports/components/AppointmentJournalUtm'),
  loading: LoadingIndicator,
});

const EvacuatorRefusalReport = Loadable({
  loader: () => import('../../modules/reports/components/EvacuatorRefusalReport'),
  loading: LoadingIndicator,
});

const TAvarcomReport = Loadable({
  loader: () => import('../../modules/reports/components/TAvarcomReport'),
  loading: LoadingIndicator,
});

const TAvarcomEvacuationReport = Loadable({
  loader: () => import('../../modules/reports/components/TAvarcomEvacuationReport'),
  loading: LoadingIndicator,
});

const TBankInvoiceCheckReport = Loadable({
  loader: () => import('../../modules/reports/components/TBankInvoiceCheckReport'),
  loading: LoadingIndicator,
});

const OrderUtm = Loadable({
  loader: () => import('../../modules/reports/components/OrderUtm'),
  loading: LoadingIndicator,
});

const SovkombankAppointmentsReport = Loadable({
  loader: () => import('../../modules/reports/components/SovkombankAppointmentsReport'),
  loading: LoadingIndicator,
});

const TireServiceOrdersReport = Loadable({
  loader: () => import('../../modules/reports/components/tireServiceOrders/TireServiceOrdersReport'),
  loading: LoadingIndicator,
});

const TireStorageOrdersReport = Loadable({
  loader: () => import('../../modules/reports/components/tireStorage/TireStorageOrdersReport'),
  loading: LoadingIndicator,
});

const ClientStatisticReport = Loadable({
  loader: () => import('../../modules/reports/components/clientStatistic/ClientStatisticReport'),
  loading: LoadingIndicator,
});

const ReconciliationReport = Loadable({
  loader: () => import('../../modules/reconciliations/ReconciliationReport'),
  loading: LoadingIndicator,
});

const PromoCheckDateRangeReport = Loadable({
  loader: () => import('../../modules/reports/components/PromoCheckDateRangeReport'),
  loading: LoadingIndicator,
});

const BonusCheckDateRangeReport = Loadable({
  loader: () => import('../../modules/reports/components/BonusCheckDateRangeReport'),
  loading: LoadingIndicator,
});

const WheelsStorageReport = Loadable({
  loader: () => import('../../modules/reports/components/WheelsStorageReport'),
  loading: LoadingIndicator,
});

const ClientReportForMarketing = Loadable({
  loader: () => import('../../modules/reports/components/ClientReportForMarketing'),
  loading: LoadingIndicator,
});

const PersonalDataReport = Loadable({
  loader: () => import('../../modules/reports/components/PersonalDataReport'),
  loading: LoadingIndicator,
});

const CheckTyreServiceReport = Loadable({
  loader: () => import('../../modules/reports/components/CheckTyreServiceReport'),
  loading: LoadingIndicator,
});

const TyreChangesReport = Loadable({
  loader: () => import('../../modules/reports/components/TyreChangesReport'),
  loading: LoadingIndicator,
});

const SchedulerDurationReport = Loadable({
  loader: () => import('../../modules/reports/components/SchedulerDurationReport'),
  loading: LoadingIndicator,
});

const PaidInvoiceReport = Loadable({
  loader: () => import('../../modules/reports/components/PaidInvoices'),
  loading: LoadingIndicator,
});

const TireServiceReport = Loadable({
  loader: () => import('../../modules/reports/components/TireServiceReport'),
  loading: LoadingIndicator,
});

const LossAdjustmentUserReport = Loadable({
  loader: () => import('../../modules/reports/components/LossAdjustmentUserReport'),
  loading: LoadingIndicator,
});

const TBankLegalReport = Loadable({
  loader: () => import('../../modules/reports/tbank/reports/TBankLegalReport'),
  loading: LoadingIndicator,
});

const TBankMultiPaymentReport = Loadable({
  loader: () => import('../../modules/reports/tbank/reports/TBankMultiPaymentReport'),
  loading: LoadingIndicator,
});

const LossAdjustmentReport = Loadable({
  loader: () => import('../../modules/reports/components/LossAdjustmentReport'),
  loading: LoadingIndicator,
});

const OperatorStatistics = Loadable({
  loader: () => import('../../modules/reports/components/OperatorStatistics'),
  loading: LoadingIndicator,
});

const CardTypeList = withTitle({
  title: 'Сервисные карты',
  component: Loadable({
    loader: () => import('../../modules/service-cards/references/CardTypeList'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const PromoList = withTitle({
  title: 'Промоакции',
  component: Loadable({
    loader: () => import('../../modules/promos/reference/PromoList'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const SeoPages = withTitle({
  title: 'SEO страницы',
  component: Loadable({
    loader: () => import('../../modules/seo/reference/SeoPages'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const SeoBlog = withTitle({
  title: 'SEO Blog',
  component: Loadable({
    loader: () => import('../../modules/seo/reference/SeoBlog'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const BannerList = withTitle({
  title: 'Баннеры ',
  component: Loadable({
    loader: () => import('../../modules/seo/reference/BannerList'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const InformerList = withTitle({
  title: 'Информеры',
  component: Loadable({
    loader: () => import('../../modules/prices/components/informer/InformerList'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const TyreReference = withTitle({
  title: 'Шины ',
  component: Loadable({
    loader: () => import('../../modules/references/tyres/TyreReference'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const ReviewList = withTitle({
  title: 'Отзывы',
  component: Loadable({
    loader: () => import('../../modules/seo/review/ReviewList'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const PostOfficeList = withTitle({
  title: 'Посты',
  component: Loadable({
    loader: () => import('../../modules/scheduler/reference/PostOfficeList'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const WeekScheduler = withTitle({
  title: 'Расписание на неделю',
  component: Loadable({
    loader: () => import('../../modules/scheduler/reference/WeekScheduler'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const HalfYearScheduler = withTitle({
  title: 'Расписание на полгода',
  component: Loadable({
    loader: () => import('../../modules/scheduler/main/HalfYearScheduler'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const SchedulerFederal = withTitle({
  title: 'Журнал записи',
  component: Loadable({
    loader: () => import('../../modules/scheduler/main/SchedulerFederal'),
    loading: LoadingIndicatorSkeleton,
  }),
});

// DEV-стенд планировщиков V2 (замена dx-scheduler, трек SCHEDULER_REPLACEMENT_PLAN.md).
// В прод-сборке маршрут не монтируется; после фазы 3 (перенос экранов) стенд удалить.
const SchedulerV2Stand = withTitle({
  title: 'Стенд планировщиков V2',
  component: Loadable({
    loader: () => import('../../modules/scheduler/componentsV2/SchedulerV2Stand'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const SchedulerTimeline = withTitle({
  title: 'Журнал записи',
  component: Loadable({
    loader: () => import('../../modules/scheduler/main/SchedulerTimeline'),
    loading: LoadingIndicatorSkeleton,
  }),
});

const SchedulerDeletedAppointmentsReport = Loadable({
  loader: () => import('../../modules/scheduler/main/SchedulerDeletedAppointmentsReport'),
  loading: LoadingIndicator,
});

const TireTransferPage = Loadable({
  loader: () => import('../../modules/scheduler/main/TireTransferPage'),
  loading: LoadingIndicator,
});

const FifthWheelAppointmentsPage = Loadable({
  loader: () => import('../../modules/scheduler/main/FifthWheelAppointmentsPage'),
  loading: LoadingIndicator,
});

const QRCodePrintForm = Loadable({
  loader: () => import('../../modules/orders/components/tireDefects/QRCodePrintForm'),
  loading: LoadingIndicator,
});

const PriceListOfficeEditForm = withTitle({
  title: 'Привязка офиса к прайс-листу',
  component: Loadable({
    loader: () => import('../../modules/prices/components/PriceListOfficeEditForm'),
    loading: LoadingIndicator,
  }),
});

const SovcombankCheck = withTitle({
  title: 'Проверка Совкомбанк',
  component: Loadable({
    loader: () => import('../../modules/prices/components/SovcombankControlPanel'),
    loading: LoadingIndicator,
  }),
});

export default (
  <Routes>
    <Route path='/' element={ <UserHasPermissionV2 componentToPassDown={ <DashBoard /> } /> } />

    { /* -----  USERS SECTION -------  */ }
    <Route
      path={ URL.URL_USERS }
      element={ <UserHasPermissionV2 componentToPassDown={ <UsersList /> } operationKey='reference.user' /> }
    />
    <Route
      path={ URL.URL_USERS_VIEW_ONE_C }
      element={ (
        <UserHasPermissionV2
          componentToPassDown={ <UserEditOne /> }
          operationKey='reference.user'
          permissionType={ [
            'permissionUpdate',
            'permissionDelete',
          ] }
        />
      ) }
    />

    { /* -----  PERMISSIONS AND ROLES SECTION -------  */ }
    <Route path={ URL.URL_SERVICES } element={ <UserHasPermissionV2 componentToPassDown={ <Services /> } operationKey='reference.service' /> } />
    <Route path={ URL.URL_POSITIONS } element={ <UserHasPermissionV2 componentToPassDown={ <Positions /> } operationKey='reference.position' /> } />
    <Route path={ URL.URL_PRICELISTS } element={ <UserHasPermissionV2 componentToPassDown={ <PriceLists /> } operationKey='reference.pricelist' /> } />
    { /*  // TODO - PriceListOne  не имеет кода безопасности, обсудить и сделать, если надо  */ }
    <Route
      path={ URL.URL_PRICELISTS_VIEW_ONE_C }
      element={ <UserHasPermissionV2 componentToPassDown={ <PriceListOne /> } /> }
    />
    <Route path={ URL.URL_ROLES } element={ <UserHasPermissionV2 componentToPassDown={ <Roles /> } operationKey='reference.role' /> } />
    <Route path={ URL.URL_ROLE_EDIT } element={ <UserHasPermissionV2 componentToPassDown={ <RoleEdit /> } operationKey='reference.role' /> } />
    <Route path={ URL.URL_OPERATIONS } element={ <UserHasPermissionV2 componentToPassDown={ <Operations /> } operationKey='reference.operation' /> } />
    <Route path={ URL.URL_PERMISSIONS } element={ <UserHasPermissionV2 componentToPassDown={ <Permissions /> } operationKey='reference.permission' /> } />
    <Route path={ URL.URL_PARTNER_PERMISSIONS } element={ <UserHasPermissionV2 componentToPassDown={ <PartnerPermissions /> } operationKey='reference.partner.permission' /> } />
    <Route path={ URL.URL_FEEDBACK } element={ <UserHasPermissionV2 componentToPassDown={ <Feedback /> } operationKey='feedback.console.manage' /> } />
    { /* «Мои обращения» — без operationKey: доступно любому авторизованному, backend отдаёт только свои */ }
    <Route path={ URL.URL_FEEDBACK_MY } element={ <UserHasPermissionV2 componentToPassDown={ <MyFeedback /> } /> } />
    <Route path={ URL.URL_PERMISSIONS_BY_OP_С } element={ <UserHasPermissionV2 componentToPassDown={ <PermissionByOperation /> } operationKey='reference.permission' /> } />

    { /* -----  PARTNERS SECTION -------  */ }
    <Route
      path={ URL.URL_PARTNERS }
      element={ <UserHasPermissionV2 componentToPassDown={ <PartnersList /> } operationKey='reference.partner' /> }
    />
    <Route
      path={ URL.URL_PARTNERS_CREATE_ONE }
      element={ <UserHasPermissionV2 componentToPassDown={ <PartnerCreateOne /> } operationKey='reference.partner' permissionType='permissionInsert' /> }
    />
    <Route
      path={ URL.URL_PARTNERS_VIEW_ONE_C }
      element={ (
        <UserHasPermissionV2
          componentToPassDown={ <PartnerEditOne /> }
          operationKey='reference.partner'
          permissionType={ [
            'permissionUpdate',
            'permissionDelete',
          ] }
        />
      ) }
    />
    <Route
      path={ URL.URL_DIVISIONS }
      element={ <UserHasPermissionV2 componentToPassDown={ <PartnerDivisionList /> } operationKey='reference.partner.division' /> }
    />

    <Route
      path={ URL.URL_DOC_TEMPLATES }
      element={ (
        <UserHasPermissionV2
          componentToPassDown={ <DocTemplatesPage /> }
          operationKey='reference.partner'
        />
      ) }
    />
    <Route
      path={ URL.URL_INSURANCE }
      element={ <UserHasPermissionV2 componentToPassDown={ <InsuranceList /> } /> }
    />
    { /*  // TODO - InsuranceChanges  не имеет кода безопасности, обсудить и сделать, если надо  */ }
    <Route
      path={ URL.URL_INSURANCE_CHANGES }
      element={ <UserHasPermissionV2 componentToPassDown={ <InsuranceChanges /> } /> }
    />
    { /*  // TODO - VehicleChanges  не имеет кода безопасности, обсудить и сделать, если надо  */ }
    <Route
      path={ URL.URL_VEHICLE_CHANGES }
      element={ <UserHasPermissionV2 componentToPassDown={ <VehicleChanges /> } /> }
    />
    <Route
      path={ URL.URL_VEHICLE_CHANGES_C }
      element={ <UserHasPermissionV2 componentToPassDown={ <VehicleChanges /> } /> }
    />

    { /* -----  ORDERS SECTION -------  */ }
    <Route
      path={ URL.URL_ORDERS }
      element={ safe(
        <UserHasPermissionV2
          componentToPassDown={ <OrdersList statusRefValues={ [27, 28, 121, 146, 147, 148, 149, 181, 182, 183] } /> }
          operationKey='order.active'
        />,
        'orders-list'
      ) }
    />
    <Route
      path={ URL.URL_ORDERS_ARCHIVE }
      element={ safe(
        <UserHasPermissionV2
          componentToPassDown={ <OrdersList statusRefValues={ [29] } /> }
          operationKey='order.archive'
        />,
        'orders-archive'
      ) }
    />

    <Route
      path={ URL.URL_ORDERS_COMMUNICATION_EXPIRED }
      element={ safe(
        <UserHasPermissionV2
          componentToPassDown={ <OrderCommunicationExpired /> }
          operationKey='order.communication'
        />,
        'orders-communication-expired'
      ) }
    />

    <Route
      path={ URL.URL_EVACUATOR_ORDERS }
      element={ safe(
        <UserHasPermissionV2
          componentToPassDown={ <EvacuatorOrders /> }
          operationKey='order.evacuator'
        />,
        'evacuator-orders'
      ) }
    />

    <Route
      path={ URL.URL_AVARCOM_ORDERS }
      element={ safe(
        <UserHasPermissionV2
          componentToPassDown={ <AvarcomOrders /> }
          operationKey='order.avarcom'
        />,
        'avarcom-orders'
      ) }
    />

    <Route
      path={ URL.URL_TBANK_AVARCOM_ORDERS }
      element={ safe(
        <UserHasPermissionV2
          componentToPassDown={ <TBankAvarcomOrders /> }
          operationKey='order.tbank.avarcom'
        />,
        'tbank-avarcom-orders'
      ) }
    />

    <Route
      path={ URL.URL_TBANK_AVARCOM_EVACUATION_ORDERS }
      element={ safe(
        <UserHasPermissionV2
          componentToPassDown={ <TBankEvacuationOrders /> }
          operationKey='order.tbank.avarcom'
        />,
        'tbank-evacuation-orders'
      ) }
    />

    <Route
      path={ URL.URL_ORDERS_FIND_BY_VALUES }
      element={ safe(
        <UserHasPermissionV2
          componentToPassDown={ <OrdersByValues /> }
          operationKey='order.find.massive'
        />,
        'orders-find-by-values'
      ) }
    />

    <Route
      path={ URL.URL_USERS_ONLINE }
      element={ (
        <UserHasPermissionV2
          componentToPassDown={ <UsersOnline /> }
          operationKey='user.online'
        />
      ) }
    />

    <Route
      path={ URL.URL_TIRE_FITTING_ORDERS }
      element={ safe(
        <UserHasPermissionV2
          componentToPassDown={ <TireFittingOrders /> }
          operationKey='order.tire.fitting'
        />,
        'tire-fitting-orders'
      ) }
    />

    <Route
      path={ URL.URL_LOSS_ADJUSTMENT_ORDERS }
      element={ safe(
        <UserHasPermissionV2
          componentToPassDown={ <LossAdjustmentOrders /> }
          operationKey='order.settlement.losses'
        />,
        'loss-adjustment-orders'
      ) }
    />

    <Route
      path={ URL.URL_STATUS_NOTICE_APPOINTMENTS }
      element={ safe(
        <UserHasPermissionV2
          componentToPassDown={ <StatusNoticeList /> }
          operationKey='scheduler.status.notice'
        />,
        'scheduler-status-notice'
      ) }
    />

    <Route
      path={ URL.URL_PARTNER_CAR_PARK }
      element={ (
        <UserHasPermissionV2
          componentToPassDown={ <PartnersList
            header='Автопарк партнёров'
            hidePriorities
            hideCreate
            carParkMode
          /> }
          operationKey='reference.partner.car.park'
        />
      ) }
    />

    <Route
      path={ URL.URL_ORDERS_COMMUNICATION_EXPIRED_BY_OFFICE }
      element={ safe(
        <UserHasPermissionV2
          componentToPassDown={ <OrderCommunicationExpiredByOffice /> }
          operationKey='order.communication'
        />,
        'orders-communication-expired-by-office'
      ) }
    />

    <Route
      path={ URL.URL_ORDERS_CREATE_ONE }
      element={ safe(
        <UserHasPermissionV2 componentToPassDown={ <OrderCreateOne /> } />,
        'orders-create'
      ) }
    />
    { /* TODO - надо  прописать тут права по окончанию работ с заявкой */ }
    <Route
      path={ URL.URL_ORDERS_VIEW_ONE_C }
      element={ safe(
        <UserHasPermissionV2 componentToPassDown={ <OrderEditOne /> } />,
        'orders-edit'
      ) }
    />
    <Route
      path={ URL.URL_ORDERS_VIEW_ONE_EVACUATOR_C }
      element={ safe(
        <UserHasPermissionV2 componentToPassDown={ <OrderEditEvacuatorOne /> } />,
        'orders-edit-evacuator'
      ) }
    />
    <Route
      path={ URL.URL_ORDERS_VIEW_ONE_TECH_SERVICE_C }
      element={ safe(
        <UserHasPermissionV2 componentToPassDown={ <OrderEditTechServiceOne /> } />,
        'orders-edit-tech-service'
      ) }
    />
    <Route
      path={ URL.URL_TIRE_FITTING_ORDER_VIEW_ONE_C }
      element={ safe(
        <UserHasPermissionV2 componentToPassDown={ <OrderEditTireFittingOne /> } />,
        'orders-edit-tire-fitting'
      ) }
    />
    <Route
      path={ URL.URL_TIRE_STORAGE_ORDER_VIEW_ONE_C }
      element={ safe(
        <UserHasPermissionV2 componentToPassDown={ <OrderEditTireStorageOne /> } />,
        'orders-edit-tire-storage'
      ) }
    />

    <Route
      path={ URL.URL_LOSS_ADJUSTMENT_ORDER_VIEW_ONE_C }
      element={ safe(
        <UserHasPermissionV2 componentToPassDown={ <OrderEditLossAdjustmentOne /> } />,
        'orders-edit-loss-adjustment'
      ) }
    />
    <Route
      path={ URL.URL_ORDER_DOC_PREVIEW_C }
      element={ safe(
        <UserHasPermissionV2 componentToPassDown={ <OrderDocPreviewPage /> } />,
        'orders-doc-preview'
      ) }
    />
    <Route
      path={ URL.URL_ORDERS_VIEW_ONE_C_AUTO }
      element={ safe(
        <UserHasPermissionV2 componentToPassDown={ <OrderEditOne /> } />,
        'orders-edit-auto'
      ) }
    />
    { /* TODO - надо  прописать тут права по окончанию работ с заявкой */ }

    { /* -----  VZR SECTION -------  */ }
    <Route
      path={ URL.URL_VZR_POLICIES }
      element={ <UserHasPermissionV2 componentToPassDown={ <VzrPoliciesList /> } operationKey='vzr.policies' /> }
    />
    <Route
      path={ URL.URL_VZR_POLICY_CREATE_ONE }
      element={ <UserHasPermissionV2 componentToPassDown={ <VzrPolicyCreateOne /> } operationKey='vzr.policies' permissionType='permissionInsert' /> }
    />
    <Route
      path={ URL.URL_VZR_POLICY_VIEW_ONE_C }
      element={ <UserHasPermissionV2 componentToPassDown={ <VzrPoliciesViewOne /> } operationKey='vzr.policies' permissionType='permissionUpdate' /> }
    />
    <Route
      path={ URL.URL_VZR_PAYMENTS }
      element={ <UserHasPermissionV2 componentToPassDown={ <VzrPaymentsList /> } operationKey='vzr.payments' /> }
    />
    <Route
      path={ URL.URL_VZR_PAYMENT_CREATE_ONE }
      element={ <UserHasPermissionV2 componentToPassDown={ <VzrPaymentEditOne /> } operationKey='vzr.payments' /> }
    />
    <Route
      path={ URL.URL_VZR_PAYMENT_VIEW_ONE_C }
      element={ <UserHasPermissionV2 componentToPassDown={ <VzrPaymentEditOne /> } operationKey='vzr.payments' /> }
    />
    <Route
      path={ URL.URL_VZR_PROMOCODES }
      element={ <UserHasPermissionV2 componentToPassDown={ <VzrPromoCodesList /> } operationKey='vzr.promocodes' /> }
    />
    <Route
      path={ URL.URL_VZR_PROMOCODES_CREATE_ONE }
      element={ <UserHasPermissionV2 componentToPassDown={ <VzrPromoCodeViewOne /> } operationKey='vzr.promocodes' permissionType='permissionInsert' /> }
    />
    <Route
      path={ URL.URL_VZR_PROMOCODES_VIEW_ONE_C }
      element={ <UserHasPermissionV2 componentToPassDown={ <VzrPromoCodeViewOne /> } operationKey='vzr.promocodes' permissionType='permissionUpdate' /> }
    />

    { /* -----  REPORTS SECTION -------  */ }
    <Route
      path={ URL.URL_REPORTS_A3_PAID_FINE }
      element={ <UserHasPermissionV2 componentToPassDown={ <A3PaidFineList /> } operationKey='report.a3PaidFine' /> }
    />
    <Route
      path={ URL.URL_REPORTS_A3_AVAILABILITY }
      element={ <UserHasPermissionV2 componentToPassDown={ <A3AvailabilityList /> } operationKey='report.a3PaidFine' /> }
    />
    <Route
      path={ URL.URL_REPORTS_CLIENT_REGISTRATION }
      element={ <UserHasPermissionV2 componentToPassDown={ <ClientRegistrationReport /> } operationKey='report.clientRegistration' /> }
    />
    <Route
      path={ URL.URL_REPORTS_SETTLEMENT_WITH_PARTNER }
      element={ <UserHasPermissionV2 componentToPassDown={ <SettlementWithPartnerReport /> } operationKey='report.payment.partners' /> }
    />
    <Route
      path={ URL.URL_UTM_REPORT_APPOINTMENTS }
      element={ <UserHasPermissionV2 componentToPassDown={ <AppointmentJournalUtm /> } operationKey='report.utm.appointment' /> }
    />
    <Route
      path={ URL.URL_T_AVARCOM_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <TAvarcomReport /> } operationKey='report.t.avarcom' /> }
    />
    <Route
      path={ URL.URL_T_AVARCOM_EVACUATION_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <TAvarcomEvacuationReport /> } operationKey='report.t.avarcom' /> }
    />
    <Route
      path={ URL.URL_T_BANK_CHECK_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <TBankInvoiceCheckReport /> } operationKey='report.tbank.check' /> }
    />
    <Route
      path={ URL.URL_PROMO_STATISTICS_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <PromoCheckDateRangeReport /> } operationKey='report.promo.statistic' /> }
    />
    <Route
      path={ URL.URL_BONUS_STATISTICS_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <BonusCheckDateRangeReport /> } operationKey='report.bonus.statistic' /> }
    />
    <Route
      path={ URL.URL_WHEELS_STORAGE_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <WheelsStorageReport /> } operationKey='report.wheels.storage' /> }
    />
    <Route
      path={ URL.URL_CLIENT_FOR_MARKETING_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <ClientReportForMarketing /> } operationKey='report.client.marketing' /> }
    />
    <Route
      path={ URL.URL_PERSONAL_DATA_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <PersonalDataReport /> } operationKey='report.personal.data' /> }
    />
    <Route
      path={ URL.URL_CHECK_TYRE_SERVICE_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <CheckTyreServiceReport /> } operationKey='report.tyre.service' /> }
    />
    <Route
      path={ URL.URL_TYRE_CHANGES_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <TyreChangesReport /> } operationKey='report.tyre.changes' /> }
    />
    <Route
      path={ URL.URL_SCHEDULER_DURATION_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <SchedulerDurationReport /> } operationKey='report.scheduler.duration' /> }
    />
    <Route
      path={ URL.URL_PAID_INVOICE_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <PaidInvoiceReport /> } operationKey='report.paid.invoice' /> }
    />    
    <Route
      path={ URL.URL_TIRE_SERVICE_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <TireServiceReport /> } operationKey='report.tire.service' /> }
    />    
    <Route
      path={ URL.URL_LOSS_ADJUSTMENT_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <LossAdjustmentReport /> } operationKey='report.loss.adjustment' /> }
    />    
    <Route
      path={ URL.URL_LOSS_ADJUSTMENT_USER_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <LossAdjustmentUserReport /> } operationKey='report.loss.adjustment.user' /> }
    />  
    <Route
      path={ URL.URL_TBANK_LEGALS_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <TBankLegalReport /> } operationKey='report.tbank.legals' /> }
    />         
    <Route
      path={ URL.URL_TBANK_MULTI_PAYMENTS }
      element={ <UserHasPermissionV2 componentToPassDown={ <TBankMultiPaymentReport /> } operationKey='report.tbank.multipayment.report' /> }
    />
    <Route
      path={ URL.URL_UTM_REPORT_ORDERS }
      element={ <UserHasPermissionV2 componentToPassDown={ <OrderUtm /> } operationKey='report.utm.order' /> }
    />
    <Route
      path={ URL.URL_SOVCOMBANK_APPOINTMENTS_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <SovkombankAppointmentsReport /> } operationKey='report.sovkombank' /> }
    />
    <Route
      path={ URL.URL_TIRE_SERVICE_ORDERS_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <TireServiceOrdersReport /> } operationKey='report.tireService.global' /> }
    />
    <Route
      path={ URL.URL_TIRE_STORAGE_ORDERS_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <TireStorageOrdersReport /> } operationKey='report.tireService.global' /> }
    />
    <Route
      path={ URL.URL_CLIENT_STATISTIC_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <ClientStatisticReport /> } operationKey='report.clientRegistration' /> }
    />
    <Route
      path={ URL.URL_EVACUATOR_REFUSAL_REPORT }
      element={ <UserHasPermissionV2 componentToPassDown={ <EvacuatorRefusalReport /> } operationKey='report.evacuator.refusal' /> }
    />
    <Route
      path={ URL.URL_REPORTS_RECONCILIATION }
      element={ <UserHasPermissionV2 componentToPassDown={ <ReconciliationReport /> } operationKey='reconciliation.act.main' /> }
    />

    <Route
      path={ URL.URL_REPORTS_RECONCILIATION_VIEW_ONE_C }
      element={ <UserHasPermissionV2 componentToPassDown={ <ReconciliationEditOne /> } operationKey='reconciliation.act.main' /> }
    />

    <Route
      path={ URL.URL_REPORTS_MOBILE_STATISTICS }
      element={ <UserHasPermissionV2 componentToPassDown={ <MobileStatisticsReport /> } operationKey='report.mobile.statistics' /> }
    />

    <Route
      path={ URL.URL_REPORTS_OPERATOR_STATISTICS }
      element={ <UserHasPermissionV2 componentToPassDown={ <OperatorStatistics /> } operationKey='report.operator.statistics' /> }
    />

    <Route
      path={ URL.URL_REPORTS_MOBILE_V2 }
      element={ <UserHasPermissionV2 componentToPassDown={ <MobileStatisticsReportV2 /> } operationKey='report.mobile.statistics' /> }
    />

    <Route
      path={ URL.URL_REPORT_BONUSES }
      element={ <UserHasPermissionV2 componentToPassDown={ <BonusesReport /> } operationKey='report.bonuses' /> }
    />

    { /* -----  Секция настроек админки и системы -------  */ }
    <Route
      path={ URL.URL_CONFIG }
      element={ <UserHasPermissionV2 componentToPassDown={ <CommonConfig /> } operationKey='lk.account.settings' /> }
    />
    <Route
      path={ URL.URL_CRON_TASK }
      element={ <UserHasPermissionV2 componentToPassDown={ <CronTaskPage /> } operationKey='lk.crontasks' /> }
    />
    <Route
      path={ URL.URL_PERSONAL_STATISTIC }
      element={ <UserHasPermissionV2 componentToPassDown={ <PersonalStatistic /> } operationKey='lk.account.settings' /> }
    />
    <Route
      path={ URL.URL_STREAM_BALANCE }
      element={ <UserHasPermissionV2 componentToPassDown={ <StreamBalance /> } operationKey='stream.account.settings' /> }
    />

    <Route
      path={ URL.URL_MAX_CHANNEL }
      element={ <UserHasPermissionV2 componentToPassDown={ <MaxChannels /> } operationKey='reference.max.channel' /> }
    />

    <Route
      path={ URL.URL_CONFIG_WORKSPACE }
      element={ <UserHasPermissionV2 componentToPassDown={ <WorkspaceConfig /> } operationKey='lk.workspace.settings' /> }
    />
    <Route
      path={ URL.URL_WEBSOCKET_LOG }
      element={ <UserHasPermissionV2 componentToPassDown={ <WebsocketLog /> } operationKey='lk.account.settings' /> }
    />

    <Route
      path={ URL.URL_UNIVERSAL_REFERENCE }
      element={ <UserHasPermissionV2 componentToPassDown={ <UniversalReference /> } operationKey='reference.universal' /> }
    />
    <Route
      path={ URL.URL_CARMEN_COMPATIBILITY }
      element={ <UserHasPermissionV2 componentToPassDown={ <CarmenMakeCompatTable /> } operationKey='reference.carmen.makes' /> }
    />
    <Route
      path={ URL.URL_CARMEN_USERS }
      element={ <UserHasPermissionV2 componentToPassDown={ <CarmenUsers /> } operationKey='reference.carmen.users' /> }
    />
    <Route
      path={ URL.URL_SERVICE_CARD_TYPE }
      element={ <UserHasPermissionV2 componentToPassDown={ <CardTypeList /> } operationKey='reference.serviceCard.type' /> }
    />
    <Route
      path={ URL.URL_PROMO_REFERENCE }
      element={ <UserHasPermissionV2 componentToPassDown={ <PromoList /> } operationKey='reference.promo' /> }
    />
    <Route
      path={ URL.URL_SEO_PAGES_REFERENCE }
      element={ <UserHasPermissionV2 componentToPassDown={ <SeoPages /> } operationKey='reference.seo.tree' /> }
    />
    <Route
      path={ URL.URL_SEO_BLOG_REFERENCE }
      element={ <UserHasPermissionV2 componentToPassDown={ <SeoBlog /> } operationKey='reference.seo.blog' /> }
    />
    <Route
      path={ URL.URL_SEO_BANNERS_REFERENCE }
      element={ <UserHasPermissionV2 componentToPassDown={ <BannerList /> } operationKey='reference.seo.banner' /> }
    />
    <Route
      path={ URL.URL_INFORMERS }
      element={ <UserHasPermissionV2 componentToPassDown={ <InformerList /> } operationKey='reference.informer' /> }
    />
    
    <Route
      path={ URL.URL_TYRE_REFERENCE }
      element={ <UserHasPermissionV2 componentToPassDown={ <TyreReference /> } operationKey='reference.tyre' /> }
    />

    <Route
      path={ URL.URL_SEO_REVIEW_REFERENCE }
      element={ <UserHasPermissionV2 componentToPassDown={ <ReviewList /> } operationKey='reference.seo.reviews' /> }
    />

    <Route
      path={ URL.URL_CLIENT_SEARCH }
      element={ <UserHasPermissionV2 componentToPassDown={ <ClientSearchPage /> } operationKey='reference.person.card' /> }
    />
    <Route
      path={ URL.URL_CLIENT_VIEW_C }
      element={ <UserHasPermissionV2 componentToPassDown={ <ClientPage /> } operationKey='reference.person.card' /> }
    />
    <Route
      path={ URL.URL_VEHICLE_SEARCH }
      element={ <UserHasPermissionV2 componentToPassDown={ <VehicleSearchByNumber /> } operationKey='reference.vehicle.findByNumber' /> }
    />
    <Route
      path={ URL.URL_VEHICLE_VIEW_C }
      element={ <UserHasPermissionV2 componentToPassDown={ <VehiclePage /> } operationKey='reference.vehicle.findByNumber' /> }
    />

    <Route
      path={ URL.URL_SCHEDULER_POST }
      element={ safe(
        <UserHasPermissionV2 componentToPassDown={ <PostOfficeList /> } operationKey='reference.post.office' />,
        'scheduler-post'
      ) }
    />

    <Route
      path={ URL.URL_SCHEDULER_WEEK }
      element={ safe(
        <UserHasPermissionV2 componentToPassDown={ <WeekScheduler /> } operationKey='reference.post.office' />,
        'scheduler-week'
      ) }
    />

    <Route
      path={ URL.URL_SCHEDULER_HALF_YEAR }
      element={ safe(
        <UserHasPermissionV2 componentToPassDown={ <HalfYearScheduler /> } operationKey='reference.post.office' />,
        'scheduler-half-year'
      ) }
    />

    <Route
      path={ URL.URL_SCHEDULER_FEDERAL }
      element={ safe(
        <UserHasPermissionV2 componentToPassDown={ <SchedulerFederal /> } operationKey='scheduler.federal' />,
        'scheduler-federal'
      ) }
    />

    { process.env.NODE_ENV === 'development' && (
      <Route path={ URL.URL_SCHEDULER_V2_STAND } element={ safe(<SchedulerV2Stand />, 'scheduler-v2-stand') } />
    ) }

    <Route
      path={ URL.URL_SCHEDULER_TIMELINE }
      element={ safe(
        <UserHasPermissionV2
          componentToPassDown={ <SchedulerTimeline
            // key обязателен: без перемонтирования useState сохранит defaultServiceId прошлого роута
            key='loss'
            headerName = 'Урегулирование убытков'
            servicesList = { [61] }
          /> }
          operationKey='scheduler.timeline'
        />,
        'scheduler-timeline-loss'
      ) }
    />

    <Route
      path={ URL.URL_SCHEDULER_TIMELINE_TIRE_SERVICE }
      element={ safe(
        <UserHasPermissionV2
          componentToPassDown={ <SchedulerTimeline
            // key обязателен: без перемонтирования useState сохранит defaultServiceId прошлого роута
            key='tire'
            headerName = 'Шинный сервис'
            // только 68: записи «с хранением» — это флаг isStorageRequired у 68-й записи,
            // отдельный выбор 69-го сервиса в фильтре не нужен
            servicesList = { [68] }
          /> }
          operationKey='scheduler.timeline.tire.service'
        />,
        'scheduler-timeline-tire'
      ) }
    />

    <Route
      path={ URL.URL_SCHEDULER_DELETED_APPOINTMENTS }
      element={ safe(
        <UserHasPermissionV2 componentToPassDown={ <SchedulerDeletedAppointmentsReport /> } operationKey='scheduler.deleted' />,
        'scheduler-deleted-appointments'
      ) }
    />

    <Route
      path={ URL.URL_SCHEDULER_TIRE_TRANSFER }
      element={ safe(
        <UserHasPermissionV2 componentToPassDown={ <TireTransferPage /> } operationKey='scheduler.tire.transfer' />,
        'scheduler-tire-transfer'
      ) }
    />

    <Route
      path={ URL.URL_SCHEDULER_TIRE_FIFTH_WHEEL }
      element={ safe(
        <UserHasPermissionV2 componentToPassDown={ <FifthWheelAppointmentsPage /> } operationKey='scheduler.tire.5wheel' />,
        'scheduler-tire-fifth-wheel'
      ) }
    />

    <Route
      path={ URL.URL_QR_CODES }
      element={ <UserHasPermissionV2 componentToPassDown={ <QRCodePrintForm /> } operationKey='qrcode.printer' /> }
    />

    { /* -----  FREE ACCESS URLs -------  */ }
    <Route path={ URL.URL_LOGIN } element={ <LoginHomePage /> } />
    <Route path={ URL.URL_SIGNUP_C } element={ <SignUpUser /> } />
    <Route path={ URL.URL_RESTOREPWD } element={ <ResetPassword /> } />
    <Route path={ URL.URL_RESTOREPWD_RESET } element={ <ResetPassword stage='password' /> } />

    { /* -----  SOVCOMBANK SECTION -------  */ }
    <Route
      path={ URL.URL_CHECK_SOVCOMBANK }
      element={ <UserHasPermissionV2 componentToPassDown={ <SovcombankCheck /> } operationKey='reference.pricelist.sovcombank.conrol' /> }
    />

    { /* -----  PRICELIST OFFICES SECTION -------  */ }
    <Route
      path={ URL.URL_PRICELIST_OFFICES }
      element={ <UserHasPermissionV2 componentToPassDown={ <PriceListOfficeEditForm /> } operationKey='reference.pricelist.offices' /> }
    />

    <Route element={ NoMatch } />
  </Routes>
);